import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useDispute, useLeadActions, useLeads, personName, LEAD_STATUS_LABEL } from "@/modules/leads/api";
import { dateTime, money } from "@/lib/format";
import { useInvoiceActions } from "@/modules/invoices/api";
import { DeleteButton } from "@/components/shared/DeleteDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DECISION_LABEL = { pending: "À décider", rejected: "Rejetée", replaced: "Remplacé", credited: "Crédité" } as const;

export default function DisputePage() {
  const { id } = useParams<{ id: string }>();
  const { data: d, isLoading } = useDispute(id);
  const { decide, resolve } = useLeadActions();
  const nav = useNavigate();
  const invoiceActions = useInvoiceActions();
  const available = useLeads({ status: "received" });
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [replacement, setReplacement] = useState<Record<string, string>>({});
  const [closeNote, setCloseNote] = useState("");

  if (isLoading) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (!d) return <p className="text-sm text-destructive">Contestation introuvable.</p>;
  const open = d.status === "open";
  const pending = d.dispute_leads.filter((dl) => dl.decision === "pending").length;

  return (
    <div>
      <Link to="/leads" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Leads</Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3"><h1 className="font-heading text-2xl font-bold">{d.number}</h1><Badge variant={open ? "destructive" : "secondary"}>{open ? "Ouverte" : "Close"}</Badge></div>
          <p className="mt-1 text-sm text-muted-foreground"><Link to={`/clients/${d.client_id}`} className="font-medium text-foreground hover:underline">{d.clients?.legal_name}</Link> · commande <Link to={`/commandes/${d.order_id}`} className="hover:underline">{d.orders?.number}</Link> · ouverte le {dateTime(d.opened_at)}</p>
          {d.reason && <p className="mt-2 text-sm">Motif : {d.reason}</p>}
        </div>
        <DeleteButton entity="dispute" id={d.id} variant="outline" onDeleted={() => nav("/leads")} />
      </div>

      <div className="space-y-3">
        {d.dispute_leads.map((dl) => {
          const l = dl.leads;
          const candidates = (available.data ?? []).filter((c) => c.product_id === l.product_id && c.review_status !== "pending");
          return (
            <section key={dl.id} className="glass rounded-2xl p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><span className="font-medium">{personName(l.lead_identities)}</span><span className="text-sm text-muted-foreground"> · {l.products?.name ?? "—"}{l.zone ? ` · ${l.zone}` : ""} · {l.lead_identities?.anonymized_at ? "" : [l.lead_identities?.phone, l.lead_identities?.email].filter(Boolean).join(" · ")}</span></div>
                <Badge variant={dl.decision === "pending" ? "destructive" : "secondary"}>{DECISION_LABEL[dl.decision]}</Badge>
              </div>
              {dl.decision !== "pending" ? (
                <p className="mt-2 text-sm text-muted-foreground">{dl.decision_note}{dl.decision === "credited" && ` — ${money(dl.credit_amount, d.currency)} à créditer`}{dl.decision === "replaced" && dl.replacement_lead_id && " — lead de remplacement attribué gratuitement"} · {dateTime(dl.decided_at)}</p>
              ) : open && (
                <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_auto]">
                  <Input aria-label={`Note pour ${personName(l.lead_identities)}`} placeholder="Note de décision" value={notes[dl.id] ?? ""} onChange={(e) => setNotes({ ...notes, [dl.id]: e.target.value })} />
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => decide.mutate({ disputeLeadId: dl.id, decision: "rejected", note: notes[dl.id] ?? "" })}>Rejeter</Button>
                    <Button size="sm" variant="outline" onClick={() => decide.mutate({ disputeLeadId: dl.id, decision: "credited", note: notes[dl.id] ?? "" })}>Créditer</Button>
                  </div>
                  <div className="flex gap-2 sm:col-span-2">
                    <Select value={replacement[dl.id] ?? ""} onValueChange={(v) => setReplacement({ ...replacement, [dl.id]: v })}>
                      <SelectTrigger aria-label={`Lead de remplacement pour ${personName(l.lead_identities)}`} className="h-9"><SelectValue placeholder={candidates.length ? "Choisir un lead de remplacement (même produit)" : "Aucun lead disponible pour ce produit"} /></SelectTrigger>
                      <SelectContent>{candidates.map((c) => <SelectItem key={c.id} value={c.id}>{personName(c.lead_identities)}{c.zone ? ` · ${c.zone}` : ""} · {LEAD_STATUS_LABEL[c.status]}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" disabled={!replacement[dl.id]} onClick={() => decide.mutate({ disputeLeadId: dl.id, decision: "replaced", note: notes[dl.id] ?? "", replacementLeadId: replacement[dl.id] })}>Remplacer</Button>
                  </div>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <section className="glass mt-4 rounded-2xl p-5">
        {open ? (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Clore la contestation</h3>
            <p className="text-sm text-muted-foreground">{pending > 0 ? `${pending} lead${pending > 1 ? "s" : ""} sans décision.` : "Toutes les décisions sont prises."}</p>
            <div className="space-y-1"><Label htmlFor="close-note">Note de clôture</Label><Textarea id="close-note" rows={2} value={closeNote} onChange={(e) => setCloseNote(e.target.value)} /></div>
            <Button disabled={pending > 0 || resolve.isPending} onClick={() => resolve.mutate({ disputeId: d.id, note: closeNote })}>Clore</Button>
          </div>
        ) : (
          <dl className="grid gap-2 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Remplacés</dt><dd className="font-semibold">{d.replaced_count}</dd></div>
            <div><dt className="text-muted-foreground">Crédités</dt><dd className="font-semibold">{d.credited_count}</dd></div>
            <div><dt className="text-muted-foreground">Rejetés</dt><dd className="font-semibold">{d.rejected_count}</dd></div>
            <div><dt className="text-muted-foreground">Avoir à émettre</dt><dd className="font-semibold text-primary" data-testid="dispute-credit">{money(d.credit_amount, d.currency)}</dd></div>
            {d.resolution_note && <div className="sm:col-span-4 text-muted-foreground">{d.resolution_note}</div>}
            <div className="sm:col-span-4 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span>Close le {dateTime(d.resolved_at)}.</span>
              {d.credit_note_id ? <Link to={`/factures/${d.credit_note_id}`} className="font-medium text-primary hover:underline">Voir l'avoir</Link>
                : Number(d.credit_amount) > 0 ? <Button size="sm" variant="outline" disabled={invoiceActions.creditNoteFromDispute.isPending} onClick={() => invoiceActions.creditNoteFromDispute.mutate({ disputeId: d.id }, { onSuccess: (id) => nav(`/factures/${id}`) })}>Créer l'avoir ({money(d.credit_amount, d.currency)} HT)</Button>
                : <span>Aucun avoir à émettre.</span>}
            </div>
          </dl>
        )}
      </section>
    </div>
  );
}
